import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { id } from 'date-fns/locale';
import Popover from '@mui/material/Popover';
import { DateRange } from 'react-date-range';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';

const PRESETS = [
  { key: '1d', label: '1 Hari', days: 0 },
  { key: '7d', label: '1 Minggu', days: 6 },
  { key: '1m', label: '1 Bulan', months: 1 },
  { key: '3m', label: '3 Bulan', months: 3 }
];

const STORE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'D MMM YYYY';

const toStore = value =>
  value ? moment(value).startOf('day').format(STORE_FORMAT) : null;

const DateRangeSiberat = ({
  params,
  setParams,
  maxDate,
  start,
  end,
  value,
  setMount
}) => {
  const startOfMonth = moment().startOf('month').toDate();
  const today = new Date();

  const paramStart = params?.[start] || null;
  const paramEnd = params?.[end] || null;

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [preset, setPreset] = useState(null);
  const [fromDate, setFromDate] = useState(
    paramStart
      ? moment(paramStart, STORE_FORMAT).toDate()
      : moment().startOf('month').toDate()
  );
  const [toDate, setToDate] = useState(
    paramEnd ? moment(paramEnd, STORE_FORMAT).toDate() : moment().toDate()
  );

  // Sync dari parent hanya jika nilai tanggal benar-benar berubah (hindari loop)
  useEffect(() => {
    const nextFrom = paramStart || null;
    const nextTo = paramEnd || null;
    const curFrom = toStore(fromDate);
    const curTo = toStore(toDate);

    if (nextFrom === curFrom && nextTo === curTo) return;

    setFromDate(
      nextFrom
        ? moment(nextFrom, STORE_FORMAT).toDate()
        : moment().startOf('month').toDate()
    );
    setToDate(nextTo ? moment(nextTo, STORE_FORMAT).toDate() : moment().toDate());
    if (!nextFrom && !nextTo) setPreset(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- compare store strings only
  }, [paramStart, paramEnd]);

  const hasValue = Boolean(fromDate && toDate);

  const displayValue = useMemo(() => {
    if (value) return value;
    if (!hasValue) return '';
    return `${moment(fromDate).format(DISPLAY_FORMAT)} – ${moment(toDate).format(
      DISPLAY_FORMAT
    )}`;
  }, [value, fromDate, toDate, hasValue]);

  const commitRange = (startDate, endDate, nextPreset = null) => {
    const nextStart = toStore(startDate);
    const nextEnd = toStore(endDate);

    setFromDate(startDate);
    setToDate(endDate);
    setPreset(nextPreset);

    if (setParams && start && end) {
      setParams(prev => {
        const base = prev && typeof prev === 'object' ? prev : params || {};
        if (base[start] === nextStart && base[end] === nextEnd) return base;
        return {
          ...base,
          [start]: nextStart,
          [end]: nextEnd
        };
      });
    }

    if (setMount) setMount();
  };

  const onOpen = e => {
    setAnchorEl(e.currentTarget);
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const onSelectDateRanges = ({ selection }) => {
    const { startDate, endDate } = selection;
    const nextStart = toStore(startDate);
    const nextEnd = toStore(endDate);
    // Abaikan event yang sama (react-date-range sering fire ulang saat ranges di-sync)
    if (nextStart === toStore(fromDate) && nextEnd === toStore(toDate)) return;

    commitRange(startDate, endDate, null);
    if (nextStart && nextEnd && nextStart !== nextEnd) {
      onClose();
    }
  };

  const applyPreset = item => {
    const endDate = moment().startOf('day').toDate();
    let startDate = moment().startOf('day').toDate();
    if (item.days != null) {
      startDate = moment().subtract(item.days, 'day').startOf('day').toDate();
    } else if (item.months != null) {
      startDate = moment().subtract(item.months, 'month').startOf('day').toDate();
    }
    commitRange(startDate, endDate, item.key);
    onClose();
  };

  const clearRange = e => {
    e.stopPropagation();
    commitRange(moment().startOf('month').toDate(), moment().toDate(), null);
  };

  return (
    <>
      <button
        type='button'
        onClick={onOpen}
        className={`date-range-web__trigger${open ? ' is-open' : ''}${
          hasValue ? ' has-value' : ''
        }`}>
        <CalendarMonthIcon fontSize='small' className='date-range-web__icon' />
        {hasValue ? (
          <span className='date-range-web__value'>{displayValue}</span>
        ) : (
          <span className='date-range-web__placeholder'>Pilih tanggal</span>
        )}
        {hasValue && (
          <span
            role='button'
            tabIndex={0}
            className='date-range-web__clear'
            onClick={clearRange}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') clearRange(e);
            }}
            aria-label='Reset rentang tanggal'>
            <CloseIcon sx={{ fontSize: 14 }} />
          </span>
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            className: 'date-range-web',
            elevation: 0
          }
        }}>
        <div className='date-range-web__header'>
          <p className='date-range-web__title'>Pilih Rentang Tanggal</p>
          {hasValue && (
            <p className='date-range-web__subtitle'>
              {moment(fromDate).format(DISPLAY_FORMAT)} –{' '}
              {moment(toDate).format(DISPLAY_FORMAT)}
            </p>
          )}
        </div>

        <div className='date-range-web__presets'>
          {PRESETS.map(item => (
            <button
              key={item.key}
              type='button'
              className={preset === item.key ? 'active' : ''}
              onClick={() => applyPreset(item)}>
              {item.label}
            </button>
          ))}
        </div>

        <div className='date-range-web__calendar'>
          <DateRange
            ranges={[
              {
                startDate: fromDate || startOfMonth,
                endDate: toDate || today,
                key: 'selection'
              }
            ]}
            onChange={onSelectDateRanges}
            maxDate={maxDate ?? new Date()}
            showMonthAndYearPickers
            moveRangeOnFirstSelection={false}
            showDateDisplay={false}
            months={2}
            direction='horizontal'
            locale={id}
            rangeColors={['#F7CE00']}
          />
        </div>
      </Popover>
    </>
  );
};

export default DateRangeSiberat;
